// The Close-month dialog (Invoices, and the customer's Billing tab imports it too): what closing
// does, the month's ledger figures, "Close anyway" revealing a required 10–500 character reason,
// and every refusal shown inline with its code, the API's message, plain advice and the details
// it named. Purely presentational — the caller owns the API call and the dialog's state (model.ts).
// The exported name and props are a stable API (the Customers area uses it).
import { Dialog } from "@nexus/ui";
import { Checkbox, Field, errorId, hintId } from "@nexus/ui";
import { useCustomerName } from "../../ui/customer-directory";
import { isMonth, longMonthLabel } from "@nexus/ui";
import { Money } from "@nexus/ui";
import {
  canConfirmClose,
  closeRefusalFrom,
  reasonProblem,
  refusalAdvice,
  type BillingPeriod,
  type CloseDialogState,
} from "./model";

export interface CloseDialogProps {
  state: CloseDialogState;
  period: BillingPeriod | null;
  onForce(force: boolean): void;
  onReason(reason: string): void;
  onConfirm(): void;
  onClose(): void;
  /** The customer's name for the title, when the caller knows it better than the directory. */
  customerName?: string | undefined;
}

function Amount({ micro }: { micro: number | null }) {
  return micro === null ? <span class="muted">Set at close</span> : <Money micro={micro} />;
}

export function CloseDialog({ state, period, onForce, onReason, onConfirm, onClose, customerName }: CloseDialogProps) {
  const directoryName = useCustomerName(state.orgId);
  const name = customerName ?? directoryName;
  const refusal = state.error ? closeRefusalFrom(state.error) : null;
  const advice = refusal ? refusalAdvice(refusal.code) : null;
  const reasonIssue = state.force ? reasonProblem(state) : null;
  const showReasonIssue = reasonIssue !== null && state.reason.length > 0;
  const month = isMonth(state.period) ? longMonthLabel(state.period) : state.period;

  return (
    <Dialog
      title={`Close ${name} · ${month}`}
      open={state.open}
      onClose={onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-danger" disabled={!canConfirmClose(state)} onClick={onConfirm}>
            {state.busy ? "Closing…" : "Close month"}
          </button>
        </>
      }
    >
      <div class="stack-sm">
        <p class="dialog-message">
          Closing prices the month's usage and freezes its invoice: its lines never change again, and usage that arrives
          later is carried into the next open month. For a billed customer the customer invoice is written in the same step.
        </p>

        {period && (
          <dl class="summary close-summary">
            <div>
              <dt>Usage</dt>
              <dd>
                <Amount micro={period.usageMicroUsd} />
              </dd>
            </div>
            <div>
              <dt>Late usage carried in</dt>
              <dd>
                <Amount micro={period.adjustmentsMicroUsd} />
              </dd>
            </div>
            <div>
              <dt>Ledger total</dt>
              <dd>
                <Amount micro={period.totalMicroUsd} />
              </dd>
            </div>
          </dl>
        )}

        {refusal && (
          <div class="banner banner-error close-refusal" role="alert">
            <p>
              <code class="error-code">{refusal.code}</code> {refusal.message}
            </p>
            {advice && <p>{advice}</p>}
            {refusal.details.length > 0 && (
              <ul>
                {refusal.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
            {refusal.forcible && <p>Tick “Close anyway” below and give a reason to close regardless.</p>}
            {refusal.retryable && !refusal.forcible && <p>This should clear on its own — try again shortly.</p>}
          </div>
        )}

        <Checkbox
          id="close-force"
          label="Close anyway (unverified or missing model prices)"
          labelText="Close anyway"
          help="close.force"
          checked={state.force}
          onChange={onForce}
        />
        {state.force && (
          <Field
            id="close-reason"
            label="Reason"
            help="close.forceReason"
            hint="10–500 characters. Kept with the month and in the audit log."
            error={showReasonIssue ? reasonIssue : null}
          >
            <textarea
              id="close-reason"
              class="input"
              rows={3}
              value={state.reason}
              aria-invalid={showReasonIssue}
              aria-describedby={showReasonIssue ? `${hintId("close-reason")} ${errorId("close-reason")}` : hintId("close-reason")}
              onInput={(e) => onReason(e.currentTarget.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
